/**
 * Locations for the rail switcher and page filters. One session-stable fetch
 * (single-flight + cached); demo mode short-circuits to seeded rows.
 */

import { Injectable, inject, signal } from '@angular/core';

import { RDX_VISITORS_ENV } from '../core/env';
import { GqlClient } from '../core/gql-client';
import { DEMO_LOCATIONS } from '../demo/demo-data';

export interface LocationOption {
  _id: string;
  name: string;
}

const LOCATIONS_QUERY = `query VisitsLocations {
  locations {
    _id
    name
  }
}`;

interface LocationsResponse {
  locations: { _id: string; name?: string | null }[] | null;
}

@Injectable({ providedIn: 'root' })
export class LocationsService {
  private readonly gql = inject(GqlClient);

  private readonly _locations = signal<LocationOption[]>([]);
  readonly locations = this._locations.asReadonly();

  private inFlight: Promise<void> | null = null;
  private loaded = false;

  ensureLoaded(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      this._locations.set(DEMO_LOCATIONS);
      this.loaded = true;
      return Promise.resolve();
    }
    this.inFlight ??= this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    const data = await this.gql.gql<LocationsResponse>(LOCATIONS_QUERY);
    const rows = (data.locations ?? [])
      .filter((l) => l._id)
      .map((l) => ({ _id: l._id, name: l.name?.trim() || 'Unnamed location' }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this._locations.set(rows);
    this.loaded = true;
  }
}
